import { useState, useMemo } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import { Row, Col, Button, Form, InputNumber, Select, Result } from 'antd';
import dropImg from '../assets/drop-here-courses_2.png';
import Course from './Course';
import classNames from 'classnames';

const SelectedCourses = ({ columns, courses, draggedCourse, removeCourse, openAddModal, rsSkillsGroup, analysis, semestersToShow, goBackAnalysis, analyzeCurricula, rsProfiles }) => {
  const [formValues, setFormValues] = useState({
    semesters: 2,
    course_sem_limit: 6,
    credits: 120,
    credit_tolerance: 5,
    profiles: [],
  });
  

  const goBack = () => {
    setFormValues({
      semesters: 2,
      course_sem_limit: 6,
      credits: 120,
      credit_tolerance: 5,
      profiles: [],
    });
    goBackAnalysis();
  };

  function printDroppable(col) {
    const columnId = col.id;
    const currentDragged = draggedCourse ? courses[draggedCourse] : null;
    const isDropDisabled = currentDragged === null ? false : col.semester === currentDragged.semester ? false : true;
    return (
      <Droppable key={columnId} droppableId={columnId} isDropDisabled={isDropDisabled}>
        {(provided, snapshot) => {
          const listClass = classNames('list-selected', {
            'drag-over': snapshot.isDraggingOver,
          });
          return (
            <div ref={provided.innerRef} {...provided.droppableProps} className={listClass}>
              {col.courseIds.map((cid, idx) => (
                <Course key={courses[cid].id} course={courses[cid]} index={idx} removeCourse={removeCourse} openAddModal={openAddModal} rsSkillsGroup={rsSkillsGroup} columnId={columnId} />
              ))}
              {provided.placeholder}
              {col.courseIds.length === 0 && <img src={dropImg} className="drop-img" alt="Drop here" />}
            </div>
          );
        }}
      </Droppable>
    );
  }

  function onFinish(values) {
    setFormValues(values);
    analyzeCurricula(values);
  }

  const profileOptions = useMemo(() => rsProfiles.map((p) => ({ label: p.name, value: p.id })), [rsProfiles]);

  return (
    <Col xs={24} lg={12} xl={8} className="base-selected">
      <h3 className="section-title">{analysis === 'settings' ? 'Profile Coverage Analysis' : 'Suggested Curricula'}</h3>
      <div className="base-selected-content">
        <div className="base-selected-content-scroll">
          {analysis === 'error' ? (
            <>
              <div className="buttons-container">
                <Button onClick={goBack} className="button">Go Back</Button>
              </div>
              <div className="vertCenter">
                <Result status="500" title="500" subTitle="Sorry, something went wrong." />
              </div>
            </>
          ) : analysis === 'new-curricula' ? (
            <div className="new-curricula">
              <div className="buttons-container">
                <Button onClick={goBack} className="button">Go Back</Button>
              </div>
              {semestersToShow >= 1 && (
                <>
                  <h5 className="zone-title first">1st year</h5>
                  <Row>
                    <Col span={12}>
                      <h6 className="semester-title">winter semester</h6>
                      {printDroppable(columns['1-w'])}
                    </Col>
                    {semestersToShow >= 2 && (
                      <Col span={12}>
                        <h6 className="semester-title">summer semester</h6>
                        {printDroppable(columns['1-s'])}
                      </Col>
                    )}
                  </Row>
                </>
              )}
              {semestersToShow >= 3 && (
                <>
                  <h5 className="zone-title second">2nd year</h5>
                  <Row>
                    <Col span={12}>
                      <h6 className="semester-title">winter semester</h6>
                      {printDroppable(columns['2-w'])}
                    </Col>
                    {semestersToShow >= 4 && (
                      <Col span={12}>
                        <h6 className="semester-title">summer semester</h6>
                        {printDroppable(columns['2-s'])}
                      </Col>
                    )}
                  </Row>
                </>
              )}
              {semestersToShow >= 5 && (
                <>
                  <h5 className="zone-title second">3rd year</h5>
                  <Row>
                    <Col span={12}>
                      <h6 className="semester-title">winter semester</h6>
                      {printDroppable(columns['3-w'])}
                    </Col>
                    {semestersToShow >= 6 && (
                      <Col span={12}>
                        <h6 className="semester-title">summer semester</h6>
                        {printDroppable(columns['3-s'])}
                      </Col>
                    )}
                  </Row>
                </>
              )}
              {semestersToShow >= 7 && (
                <>
                  <h5 className="zone-title second">4th year</h5>
                  <Row>
                    <Col span={12}>
                      <h6 className="semester-title">winter semester</h6>
                      {printDroppable(columns['4-w'])}
                    </Col>
                    {semestersToShow >= 8 && (
                      <Col span={12}>
                        <h6 className="semester-title">summer semester</h6>
                        {printDroppable(columns['4-s'])}
                      </Col>
                    )}
                  </Row>
                </>
              )}
              {semestersToShow >= 9 && (
                <>
                  <h5 className="zone-title second">5th year</h5>
                  <Row>
                    <Col span={12}>
                      <h6 className="semester-title">winter semester</h6>
                      {printDroppable(columns['5-w'])}
                    </Col>
                    {semestersToShow >= 10 && (
                      <Col span={12}>
                        <h6 className="semester-title">summer semester</h6>
                        {printDroppable(columns['5-s'])}
                      </Col>
                    )}
                  </Row>
                </>
              )}
            </div>
          ) : (
            <div className="analysis-settings">
              <h5 className="zone-title analysis">Profile Analysis Settings</h5>
              <Form name="profileAnalysis" labelWrap colon={false} labelCol={{ span: 8 }} wrapperCol={{ span: 15 }} initialValues={formValues} onFinish={onFinish} autoComplete="off">
                <Form.Item label="Years" name="semesters" rules={[{ required: true, message: 'Please fill in the number of years!' }]}>
                  <InputNumber min={1} max={5} step={1} />
                </Form.Item>

                <Form.Item label="Max. courses per semester" name="course_sem_limit" rules={[{ required: true, message: 'Please fill in the maximum courses per semester!' }]}>
                  <InputNumber min={0} step={1} />
                </Form.Item>

                <Form.Item label="ECTS Credits" required style={{ marginBottom: 0 }}>
                  <Form.Item
                    rules={[{ required: true, message: 'Please fill in the total number of credits!' }]}
                    name="credits"
                    style={{
                      display: 'inline-block',
                    }}
                  >
                    <InputNumber min={0} />
                  </Form.Item>
                  <Form.Item
                    name="credit_tolerance"
                    rules={[{ required: true, message: 'Please fill in the credit tolerance!' }]}
                    style={{
                      display: 'inline-block',
                    }}
                  >
                    <InputNumber prefix="±" min={0} />
                  </Form.Item>
                </Form.Item>

                <Form.Item label="Profile to cover" name="profiles" rules={[{ required: true, message: 'Please select at least one profile!' }]}>
                  <Select mode="multiple" placeholder="Please select" allowClear options={profileOptions} />
                </Form.Item>

                <div className="buttons-container">
                  <Button type="primary" htmlType="submit" loading={analysis === 'loading'} disabled={Object.keys(courses).length < 1}>
                    Analyze
                  </Button>
                </div>
              </Form>
            </div>
          )}
        </div>
      </div>
    </Col>
  );
};

export default SelectedCourses;
